import React, { memo, useCallback, useMemo } from 'react'
import { DialogTitle, DialogContent, Grid, DialogActions, Button } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import ResponsiveDialog from 'components/ResponsiveDialog'
import ExternalLink from 'components/ExternalLink'
import AutoCompleteChipInput from 'components/AutoCompleteChipInput'
import AutoCompleteTextField, { IAutoCompleteOption } from 'components/AutoCompleteTextField'
import TextField from 'components/TextField'
import TextArea from 'components/TextArea'
import { graphql, useLazyLoadQuery } from 'react-relay'
import { EditTimelineEntryDialog_SearchQuery } from '__generated__/EditTimelineEntryDialog_SearchQuery.graphql'
import { useSearchAutocomplete } from 'hooks/search'
import { useForm, useMutationBasedFormSubmitHandlers } from 'hooks/form'
import { EditTimelineEntryDialog_CompaniesQuery } from '__generated__/EditTimelineEntryDialog_CompaniesQuery.graphql'
import { EditTimelineEntryDialog_Query } from '__generated__/EditTimelineEntryDialog_Query.graphql'
import { EditTimelineEntryDialog_CreateMutation } from '__generated__/EditTimelineEntryDialog_CreateMutation.graphql'
import { EditTimelineEntryDialog_UpdateMutation } from '__generated__/EditTimelineEntryDialog_UpdateMutation.graphql'
import { useSkillsAutocomplete } from 'hooks/useSkillAutocomplete'
import moment from 'moment'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import Form from 'components/Form'
import FormSubmitButton from 'components/FormSubmitButton'

export const formSchema = z.object({
  entryType: z.enum(['work', 'education']),
  place: z.union([
    z.object({ value: z.string().nonempty({ message: 'Required' }), label: z.string().nonempty({ message: 'Required' }) }),
    z.string().nonempty({ message: 'Required' }),
  ]),
  title: z.string().nonempty({ message: 'Required' }),
  dateStart: z.string()
    .nonempty({ message: 'Required' })
    .regex(/^\d{4}$/, { message: 'Must be a year (YYYY)' }),
  dateEnd: z.string()
    .nullable()
    .refine(val => !val || /^\d{4}$/.test(val), { message: 'Must be a year (YYYY)' }),
  description: z.string().nullish(),
  skills: z.array(z.object({
    id: z.string().nonempty({ message: 'Required' }),
    name: z.string().nonempty({ message: 'Required' }),
  })),
}).refine(data => !data.dateEnd || (parseInt(data.dateEnd, 10) >= parseInt(data.dateStart, 10)), {
  message: 'Must be after the start year',
  path: ['dateEnd'],
})

type FormFields = z.infer<typeof formSchema>

export interface IEditTimelineEntryDialogProps {
  open: boolean
  userId: string
  timelineEntryId: string | 'newWork' | 'newEducation' | null
  onClose: () => void
}

function EditTimelineEntryDialog(props: IEditTimelineEntryDialogProps) {
  const theme = useTheme()
  const { open, onClose, timelineEntryId, userId } = props
  const isNew = timelineEntryId === 'newWork' || timelineEntryId === 'newEducation'

  const data = useLazyLoadQuery<EditTimelineEntryDialog_Query>(graphql`
    query EditTimelineEntryDialog_Query($timelineEntryId: ID, $userId: ID) {
      user: optionalNode(id: $userId) {
        ... on User {
          profile {
            industry {
              id
            }
          }
        }
      }

      timelineEntry: optionalNode(id: $timelineEntryId) {
        ... on TimelineEntry {
          id
          entryType
          place
          title
          dateStart
          dateEnd
          description
          skills {
            id
            name
          }
        }
      }
    }
  `, { timelineEntryId: isNew ? null : timelineEntryId, userId }, {
    fetchPolicy: 'store-and-network',
  })

  const industryId = data?.user?.profile?.industry?.id

  const defaultValues = useMemo<FormFields>(() => {
    if (isNew) {
      return {
        entryType: timelineEntryId === 'newWork' ? 'work' : 'education',
        place: '',
        title: '',
        dateStart: '',
        dateEnd: '',
        description: '',
        skills: [],
      }
    }

    const dateStart = data?.timelineEntry?.dateStart
    const dateEnd = data?.timelineEntry?.dateEnd

    return {
      entryType: data?.timelineEntry?.entryType || 'work',
      place: data?.timelineEntry?.place || '',
      title: data?.timelineEntry?.title || '',
      dateStart: dateStart ? moment(dateStart).format('YYYY') : '',
      dateEnd: dateEnd ? moment(dateEnd).format('YYYY') : null,
      description: data?.timelineEntry?.description || '',
      skills: data?.timelineEntry?.skills?.map(s => ({ id: s?.id, name: s?.name })) || [],
    }
  }, [isNew, timelineEntryId, data])

  const form = useForm<FormFields, EditTimelineEntryDialog_CreateMutation | EditTimelineEntryDialog_UpdateMutation>({
    enableReinitialize: true,
    defaultValues,
    resolver: zodResolver(formSchema),
    submitHandlers: useMutationBasedFormSubmitHandlers(
      isNew ? graphql`
        mutation EditTimelineEntryDialog_CreateMutation($input: CreateTimelineEntryInput!) {
          createTimelineEntry(input: $input) {
            timelineEntry {
              id

              user {
                # Refresh whole list
                ...TimelineEntriesField_User
              }
            }
          }
        }
      ` : graphql`
        mutation EditTimelineEntryDialog_UpdateMutation($input: UpdateTimelineEntryInput!) {
          updateTimelineEntry(input: $input) {
            timelineEntry {
              id

              user {
                # Refresh whole list
                ...TimelineEntriesField_User
              }
            }
          }
        }
      `,
      (values) => {
        const attributes = {
          entryType: values.entryType,
          place: typeof values.place === 'string' ? values.place : values.place.label,
          title: values.title,
          description: values.description,
          dateStart: values.dateStart ? `${values.dateStart}-01-01` : null,
          dateEnd: values.dateEnd ? `${values.dateEnd}-01-01` : null,
          skillsIds: values.skills?.map(s => s.id) || [],
          placeId: typeof values.place === 'string' ? null : values.place.value,
        }
        return {
          input: isNew ? {
            attributes,
          } : {
            timelineEntryId,
            attributes,
          },
        }
      },
      {
        successMessage: isNew ? 'Entry created' : 'Entry updated',
        verifySuccess: (result) => {
          if (isNew) {
            return Boolean((result as EditTimelineEntryDialog_CreateMutation['response'])?.createTimelineEntry?.timelineEntry)
          }
          return Boolean((result as EditTimelineEntryDialog_UpdateMutation['response'])?.updateTimelineEntry?.timelineEntry)
        },
        onSuccess: () => {
          onClose()
        },
      },
    ),
  })

  const instituteAutocomplete = useSearchAutocomplete<EditTimelineEntryDialog_SearchQuery>(
    graphql`
      query EditTimelineEntryDialog_SearchQuery($search: String) {
        institutes(name: $search, first: 10) {
          nodes {
            id
            name
          }
        }
      }
    `,
    search => ({ search }),
    autocompleteData => autocompleteData?.institutes?.nodes?.map(n => ({ label: n?.name || '', value: n?.id || '' })) || [],
  )

  const companyAutocomplete = useSearchAutocomplete<EditTimelineEntryDialog_CompaniesQuery>(
    graphql`
      query EditTimelineEntryDialog_CompaniesQuery($search: String) {
        companies(name: $search, first: 10) {
          nodes {
            id
            name
          }
        }
      }
    `,
    search => ({ search }),
    autocompleteData => autocompleteData?.companies?.nodes?.map(n => ({ label: n?.name || '', value: n?.id || '' })) || [],
  )

  const skillsAutocomplete = useSkillsAutocomplete(industryId ? [industryId] : [])
  const skillsField = form.useFormField('skills', 'selection')
  const typeField = form.useFormField('entryType', 'selection')
  const placeField = form.useFormField('place', 'selection')
  const titleField = form.useFormField('title', 'selection')
  const dateStartField = form.useFormField('dateStart', 'selection')
  const dateEndField = form.useFormField('dateEnd', 'selection')
  const descriptionField = form.useFormField('description', 'selection')

  const skillsValue = useMemo(() => (skillsField.input.value || []).map((v: { id: string, name: string }) => ({
    value: v.id,
    label: v.name,
  })), [skillsField.input.value])

  const handleSkillsChange = useCallback((value: IAutoCompleteOption[]) => {
    skillsField.input.onChange(value.map(v => ({
      id: v.value,
      name: v.label,
    })))
  }, [skillsField.input])

  const currentYear = new Date().getFullYear()
  const type = typeField.input.value

  return (
    <ResponsiveDialog open={open} onClose={onClose} maxWidth="md">
      <Form form={form}>
        <DialogTitle>
          Edit Entry
        </DialogTitle>
        <DialogContent>
          <div style={{ paddingBottom: theme.spacing(3) }}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                {type === 'work' && (
                  <AutoCompleteTextField
                    label="Company Name"
                    helperText="If your company is not in the suggested list just type it anyway and it will save"
                    freeSolo
                    {...companyAutocomplete}
                    {...placeField}
                  />
                )}

                {type === 'education' && (
                  <AutoCompleteTextField
                    label="School name"
                    helperText="If your university is not in the suggested list just type it anyway and it will save"
                    freeSolo
                    {...placeField}
                    {...instituteAutocomplete}
                  />
                )}
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField fullWidth label={type === 'work' ? 'Position' : 'Field of study'} {...titleField} />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Start year"
                  type="number"
                  inputProps={{ min: 1900, max: currentYear }}
                  {...dateStartField}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={type === 'work' ? 'End year' : 'Graduation year'}
                  type="number"
                  inputProps={{ min: 1900, max: currentYear }}
                  helperText="Leave empty if ongoing"
                  {...dateEndField}
                />
              </Grid>

              <Grid item xs={12}>
                <TextArea
                  fullWidth
                  label={type === 'work' ? 'Key Achievements' : 'Highlights'}
                  placeholder={'- achieved first thing\\n- achieved another thing\\n- this field supports markdown\\n- use dashes to make a bulleted list like this in markdown'.replace(/\\n/g, '\n')}
                  maxLength={1500}
                  helperText={(
                    <React.Fragment>
                      1500 characters max -
                      you can use <ExternalLink href="https://en.wikipedia.org/wiki/Markdown#Example" label="markdown" showExternalIcon mouseDown /> to
                      format contents.
                    </React.Fragment>
                  )}
                  {...descriptionField}
                />
              </Grid>

              <Grid item xs={12}>
                <AutoCompleteChipInput
                  {...skillsAutocomplete}
                  {...skillsField}
                  input={{
                    ...skillsField.input,
                    value: skillsValue,
                    onChange: handleSkillsChange,
                  }}
                  label="Relevant Skills (optional)"
                  fullWidth
                />
              </Grid>
            </Grid>
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} data-cy="cancel">
            Cancel
          </Button>

          <FormSubmitButton data-cy="save" />
        </DialogActions>
      </Form>
    </ResponsiveDialog>
  )
}

export default memo(EditTimelineEntryDialog)
